import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  CircularProgress,
  Container,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { get, post, put } from "src/utils/networkUtils";
import Iconify from "../../components/iconify";
import {
  handleFileUpload,
  createGuaranteeHandlers,
  createPayoutFormatChangeHandler,
  validateChallengeForm,
} from "src/utils/challengeUtils";
import GuaranteeSettings from "src/components/GuaranteeSettings";
import PayoutFormatSelect from "src/components/PayoutFormatSelect";
import MinUsersSelect from "src/components/MinUsersSelect";
import MaxUsersSelect from "src/components/MaxUsersSelect";

export default function ChallengeSetupPage() {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [guaranteeValues, setGuaranteeValues] = useState({});
  const [xpLevels, setXpLevels] = useState([]);
  const [guaranteeSettingsOpen, setGuaranteeSettingsOpen] = useState(false);

  // Determine mode based on setupId parameter
  const mode = setupId === 'create' ? 'create' : 'edit';
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    const fetchTeams = async () => {
      const response = await get("/admin/teams?competition_id=1");
      if (response.teams.length) {
        setTeams(response.teams);
      }
    };
    const fetchXpLevels = async () => {
      const response = await get("/admin/xp-level-settings");
      if (response.settings.length) {
        setXpLevels(response.settings);
      }
    };
    fetchTeams();
    fetchXpLevels();
  }, []);

  useEffect(() => {
    const fetchChallengeSetupDetail = async () => {
      try {
        setInitialLoading(true);
        if (isEditMode && setupId !== 'create') {
          const response = await get(`/admin/challenge-setups/${setupId}`);
          if (response.setup) {
            setFormData(response.setup);
          }
        } else if (isCreateMode) {
          // Set default values for create mode
          setFormData({
            type: 0, // Default to club setup
            minXpLevel: 0,
            prizePoolPercentage: 90,
            minUsers: 2,
            maxUsers: 0,
            entryFee: 100,
            isAutoGeneration: true,
            guaranteeValues: {}
          });
        }
      } catch (error) {
        console.error("Error fetching challenge setup detail:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchChallengeSetupDetail();
  }, [setupId, isEditMode, isCreateMode]);

  const handleInputChange = (field, value) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const errors = validateChallengeForm(formData);
    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }
    const apiPayload = {
      clubName: formData.clubName,
      type: formData.type ?? 0,
      minXpLevel: formData.minXpLevel,
      payoutFormat: formData.payoutFormat,
      prizePoolPercentage: formData.prizePoolPercentage,
      maxUsers: formData.maxUsers,
      minUsers: formData.minUsers,
      entryFee: formData.entryFee,
      isAutoGeneration: formData.isAutoGeneration,
      iconUrl: formData.iconUrl || null,
      bannerUrl: formData.bannerUrl || null,
      teamId: formData.teamId,
      frequency: formData.frequency ?? null,
      guaranteeValues:
        formData.guaranteeValues &&
          Object.keys(formData.guaranteeValues).length > 0
          ? formData.guaranteeValues
          : null,
    };
    try {
      setLoading(true);
      const resp = isCreateMode
        ? await post("/admin/challenge-setups", apiPayload)
        : await put(`/admin/challenge-setups/${setupId}`, apiPayload);
      if (resp.success) {
        setLoading(false);
        navigate("/dashboard/challenge-setups");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error creating/updating challenge setup:", error);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/challenge-setups?tenantId=1");
  };

  const handleViewAll = () => {
    navigate("/dashboard/club-challenges");
  };

  const handleFileUploadWrapper = async (type, event) => {
    if (isViewMode) return; // Prevent uploads in view mode
    return handleFileUpload(type, event, setFormData);
  };

  // Create handlers state
  const [guaranteeHandlers, setGuaranteeHandlers] = useState(null);
  const [payoutFormatChangeHandler, setPayoutFormatChangeHandler] = useState(null);

  // Create handlers when formData is available
  useEffect(() => {
    if (formData) {
      const handlers = createGuaranteeHandlers(
        formData,
        setFormData,
        guaranteeValues,
        setGuaranteeValues,
        setGuaranteeSettingsOpen
      );
      setGuaranteeHandlers(handlers);

      const payoutHandler = createPayoutFormatChangeHandler(
        formData,
        setFormData,
        setGuaranteeValues,
        setGuaranteeSettingsOpen,
        !isViewMode && (!formData?.status || !["open", "live", "finished"].includes(formData.status))
      );
      setPayoutFormatChangeHandler(() => payoutHandler);
    }
  }, [formData, guaranteeValues, isViewMode]);

  // Extract handlers with null safety
  const handleGuaranteeSettingsSave = guaranteeHandlers?.handleGuaranteeSettingsSave || (() => { });
  const handleGuaranteeSettingsCancel = guaranteeHandlers?.handleGuaranteeSettingsCancel || (() => { });
  const handleGuaranteeValueChange = guaranteeHandlers?.handleGuaranteeValueChange || (() => { });
  const handlePayoutFormatChange = payoutFormatChangeHandler || (() => { });

  const getPageTitle = () => {
    if (isCreateMode) return "Challenge Setup - Create New Setup";
    if (isEditMode) return `Challenge Setup ${setupId} - Edit Details`;
    if (isViewMode) return `Challenge Setup ${setupId} - View Details`;
    return "Challenge Setup";
  };

  const renderActionButtons = () => {
    if (isViewMode) {
      return (
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={handleViewAll}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            View all Club Challenges
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              backgroundColor: "#667eea",
              "&:hover": {
                backgroundColor: "#5a6fd8",
              },
            }}
          >
            {isCreateMode ? 'Create' : 'Save'}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          {isCreateMode ? 'Create' : 'Save'}
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <Helmet>
        <title>View Club</title>
      </Helmet>

      <Container>
        {loading || initialLoading || !formData ? (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header Section */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={5}
            >
              <Typography variant="h4" gutterBottom>
                {getPageTitle()}
              </Typography>
              {renderActionButtons()}
            </Stack>

            {/* Main Form */}
            <Card sx={{ p: 3 }}>
              {/* Auto-generation Toggle - Top Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: formData.isAutoGeneration ? "#e8f5e8" : "#f8f9fa",
                  border: `1px solid ${formData.isAutoGeneration ? "#4caf50" : "#e0e0e0"}`,
                  transition: "all 0.3s ease"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: formData.isAutoGeneration ? "#4caf50" : "#9e9e9e",
                      color: "white",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <Iconify
                      icon={formData.isAutoGeneration ? "eva:refresh-fill" : "eva:pause-circle-fill"}
                      sx={{ width: 20, height: 20 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                      Auto-Generation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.isAutoGeneration
                        ? "Challenges will be automatically created when the team is playing"
                        : "Challenges will be created manually only"
                      }
                    </Typography>
                  </Box>
                </Box>
                <Checkbox
                  checked={formData.isAutoGeneration || false}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      isAutoGeneration: !formData.isAutoGeneration,
                    })
                  }
                  sx={{
                    color: "#667eea",
                    "&.Mui-checked": {
                      color: "#4caf50",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 28,
                    },
                  }}
                />
              </Box>

              {/* Form Fields in Two Columns */}
              <Box sx={{ display: "flex", gap: 4 }}>
                {/* Left Column */}
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={3}>
                    {/* Type Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Type:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.type ?? 0}
                          onChange={(e) => handleInputChange("type", parseInt(e.target.value))}
                          disabled={isEditMode}
                          sx={{
                            borderRadius: 5,
                            backgroundColor: isEditMode ? "#f5f5f5" : "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={0}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip
                                label="Club"
                                size="small"
                                sx={{
                                  backgroundColor: "#2196f3",
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                Club Challenge
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value={1}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip
                                label="Public"
                                size="small"
                                sx={{
                                  backgroundColor: "#ff9800",
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                Public Challenge
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Name Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Name:
                      </Typography>
                      <TextField
                        value={formData.clubName || ""}
                        onChange={(e) => handleInputChange("clubName", e.target.value)}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 5,
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Box>

                    {/* Select Team Field - Hidden for Public Clubs */}
                    {formData.type !== 1 && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Select Team:
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={formData.teamId || ""}
                            onChange={(e) => handleInputChange("teamId", e.target.value)}
                            disabled={isEditMode}
                            sx={{
                              borderRadius: 5,
                              backgroundColor: "white",
                              "& .MuiSelect-icon": {
                                color: "#667eea",
                              },
                            }}
                          >
                            {teams.map((team) => (
                              <MenuItem key={team.scoreableId} value={team.externalRef}>
                                {team.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    {/* Min XP Level Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Min XP Level:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.minXpLevel || 0}
                          onChange={(e) =>
                            handleInputChange("minXpLevel", parseInt(e.target.value))
                          }
                          sx={{
                            borderRadius: 5,
                            backgroundColor: "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={0}>Any</MenuItem>
                          {xpLevels.map((level) => {
                            return (
                              <MenuItem key={level.levelId} value={level.levelId}>
                                Level {level.levelName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Entry Fee Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Coin entry:
                      </Typography>
                      <TextField
                        value={formData.entryFee || ""}
                        onChange={(e) =>
                          handleInputChange("entryFee", parseInt(e.target.value) || 0)
                        }
                        disabled={isEditMode}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 5,
                            backgroundColor: isEditMode ? "#f5f5f5" : "white",
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <img
                              src="/assets/icons/ufcoin.svg"
                              alt="dollar"
                              style={{ width: 20, height: 20, marginRight: 20 }}
                            />
                          ),
                        }}
                      />
                    </Box>

                    {/* Min Users Field */}
                    <Box>
                      <MinUsersSelect
                        value={formData.minUsers}
                        onChange={(value) => handleInputChange("minUsers", value)}
                        fullWidth
                        editmode={isEditMode}
                      />
                    </Box>
                  </Stack>
                </Box>

                {/* Right Column */}
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={3}>
                    {/* Payout Format Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Payout Format:
                      </Typography>
                      <PayoutFormatSelect
                        value={formData.payoutFormat}
                        onChange={handlePayoutFormatChange}
                        guaranteeValues={formData.guaranteeValues}
                        hasEmptyState={true}
                        disabled={isEditMode}
                      />
                    </Box>

                    {/* Prize Pool Percentage Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Prize Pool:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.prizePoolPercentage || 90}
                          onChange={(e) =>
                            handleInputChange(
                              "prizePoolPercentage",
                              parseInt(e.target.value)
                            )
                          }
                          disabled={isEditMode}
                          sx={{
                            borderRadius: 5,
                            backgroundColor: isEditMode ? "#f5f5f5" : "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={100}>100% (no commission)</MenuItem>
                          <MenuItem value={95}>95%</MenuItem>
                          <MenuItem value={92}>92%</MenuItem>
                          <MenuItem value={90}>90%</MenuItem>
                          <MenuItem value={85}>85%</MenuItem>
                          <MenuItem value={80}>80%</MenuItem>
                          <MenuItem value={20}>20%</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Max Users Field */}
                    <Box>
                      <MaxUsersSelect
                        value={formData.maxUsers}
                        onChange={(value) => handleInputChange("maxUsers", value)}
                        fullWidth
                        editmode={isEditMode}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Frequency:
                      </Typography>
                      <FormControl fullWidth>

                        <Select
                          value={formData.frequency ?? 0}
                          onChange={(e) =>
                            handleInputChange(
                              "frequency",
                              parseInt(e.target.value)
                            )
                          }
                          sx={{
                            borderRadius: 5,
                            backgroundColor: "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={8}>Daily</MenuItem>
                          <MenuItem value={1}>Monday</MenuItem>
                          <MenuItem value={2}>Tuesday</MenuItem>
                          <MenuItem value={3}>Wednesday</MenuItem>
                          <MenuItem value={4}>Thursday</MenuItem>
                          <MenuItem value={5}>Friday</MenuItem>
                          <MenuItem value={6}>Saturday</MenuItem>
                          <MenuItem value={7}>Sunday</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Image Upload Sections */}
              <Box sx={{ display: "flex", gap: 4, mt: 4, flexDirection: "column" }}>
                {/* Icon Upload */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, width: "150px" }}
                  >
                    Icon: (optional)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        width: 212,
                        height: 210,
                        borderRadius: 2,
                        border: "2px dashed #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        overflow: "hidden",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {formData.iconUrl ? (
                        <img
                          src={formData.iconUrl}
                          alt="icon 1"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Iconify
                          icon="eva:image-outline"
                          sx={{
                            color: "#ccc",
                            width: 100,
                            height: 100,
                          }}
                        />
                      )}
                    </Box>
                    {!isViewMode && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUploadWrapper("icon", e)}
                          style={{ display: "none" }}
                          id="icon-upload"
                        />
                        <label
                          style={{ textAlign: "center" }}
                          htmlFor="icon-upload"
                        >
                          <Typography
                            component="span"
                            sx={{
                              textAlign: "center",
                              color: "#667eea",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "#5a6fd8",
                              },
                            }}
                          >
                            Upload
                          </Typography>
                        </label>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Banner Upload */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, width: "150px" }}
                  >
                    Banner: (optional)
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        height: 180,
                        width: 660,
                        borderRadius: 2,
                        border: "2px dashed #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        overflow: "hidden",
                        backgroundColor: "#f8f9fa",
                        position: "relative",
                      }}
                    >
                      {formData.bannerUrl ? (
                        <img
                          src={formData.bannerUrl}
                          alt="Banner 1"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Iconify
                          icon="eva:image-outline"
                          sx={{ width: 120, height: 120, color: "#ccc" }}
                        />
                      )}
                    </Box>
                    {!isViewMode && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUploadWrapper("banner1", e)}
                          style={{ display: "none" }}
                          id="banner1-upload"
                        />
                        <label htmlFor="banner1-upload">
                          <Typography
                            component="span"
                            sx={{
                              color: "#667eea",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "#5a6fd8",
                              },
                            }}
                          >
                            Upload
                          </Typography>
                        </label>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>


            </Card>

            {/* Guarantee Settings Card */}
            <GuaranteeSettings
              formData={formData}
              guaranteeValues={guaranteeValues}
              guaranteeSettingsOpen={guaranteeSettingsOpen}
              onGuaranteeValueChange={handleGuaranteeValueChange}
              onSave={handleGuaranteeSettingsSave}
              onCancel={handleGuaranteeSettingsCancel}
            />
          </>
        )}
      </Container>
    </>
  );
} 